import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientsAPI, locationsAPI, doctorsAPI, visitsAPI, filterUserLocations } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Search, Plus, Edit, User, UserCheck, Calendar, Phone, Mail, MapPin, CreditCard, Save, X, Download, Database, Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DataPagination from '../components/DataPagination';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Patients = () => {
  const navigate = useNavigate();
  const { user, clinic, isClinicAdmin, isFrontDesk, workingLocation } = useAuth();
  const [patients, setPatients] = useState([]);
  const [locations, setLocations] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [activeVisits, setActiveVisits] = useState({}); // Track patients with active visits
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [checkInDialogOpen, setCheckInDialogOpen] = useState(false);
  const [checkingIn, setCheckingIn] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [selectedPatientForCheckIn, setSelectedPatientForCheckIn] = useState(null);
  const [inlineEditing, setInlineEditing] = useState(null);
  const [inlineFormData, setInlineFormData] = useState({});
  
  // EHR Search state (integrated with main search)
  const [ehrResults, setEhrResults] = useState([]);
  const [ehrSearching, setEhrSearching] = useState(false);
  const [ehrImporting, setEhrImporting] = useState(null); // Track which patient is being imported
  const [pfOnlyMode, setPfOnlyMode] = useState(false); // Practice Fusion only search mode
  
  // Click-to-search: no typeahead. submittedQuery holds the last searched term.
  const [submittedQuery, setSubmittedQuery] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [openAccordionId, setOpenAccordionId] = useState(undefined);

  // Reset to page 1 whenever a new search is submitted
  useEffect(() => {
    setCurrentPage(1);
  }, [submittedQuery]);

  
  const [formData, setFormData] = useState({
    full_name: '',
    date_of_birth: '',
    phone: '',
    email: '',
    address: '',
    insurance_provider: '',
    insurance_number: ''
  });

  const [checkInData, setCheckInData] = useState({
    doctor_id: '',
    visit_type: 'FU'
  });

  // Check if visit type requires doctor assignment
  const requiresDoctor = checkInData.visit_type !== 'NO';

  const canEdit = isClinicAdmin() || isFrontDesk();
  const hasPracticeFusion = clinic?.practice_fusion_enabled && clinic?.has_practice_fusion_configured;

  // Start inline editing
  const startInlineEdit = (patient) => {
    setInlineEditing(patient.id);
    setInlineFormData({
      full_name: patient.full_name || '',
      date_of_birth: patient.date_of_birth || '',
      phone: patient.phone || '',
      email: patient.email || '',
      address: patient.address || '',
      insurance_provider: patient.insurance_provider || '',
      insurance_number: patient.insurance_number || ''
    });
  };

  // Cancel inline editing
  const cancelInlineEdit = () => {
    setInlineEditing(null);
    setInlineFormData({});
  };

  // Save inline edit
  const saveInlineEdit = async (patientId) => {
    try {
      await patientsAPI.update(patientId, inlineFormData);
      toast.success('Patient updated successfully');
      setInlineEditing(null);
      setInlineFormData({});
      fetchData();
    } catch (error) {
      toast.error('Failed to update patient');
    }
  };

  useEffect(() => {
    fetchStaticData();
  }, []);

  useEffect(() => {
    if (pfOnlyMode) {
      if (submittedQuery && submittedQuery.length >= 2) {
        searchPracticeFusionOnly(submittedQuery);
      } else {
        setEhrResults([]);
        setPatients([]);
      }
      return;
    }
    if (submittedQuery) {
      searchPatientsWithEhrFallback(submittedQuery);
    } else {
      fetchPatientData();
      setEhrResults([]);
    }
  }, [currentPage, pageSize, submittedQuery, pfOnlyMode]);

  // Practice Fusion only search function - searches by name, EHR number, or DOB (no phone)
  const searchPracticeFusionOnly = async (searchTerm) => {
    if (!hasPracticeFusion) {
      toast.error('Practice Fusion is not configured');
      return;
    }

    if (!searchTerm || searchTerm.length < 2) {
      return;
    }

    setEhrSearching(true);
    setEhrResults([]);
    setPatients([]); // Clear local results in PF-only mode
    
    try {
      // Build the search request body
      // Detect if it's an EHR number, DOB, or a name
      const searchBody = {};
      
      // Check if it looks like a date (YYYY-MM-DD or MM/DD/YYYY or MM-DD-YYYY)
      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}$/,  // YYYY-MM-DD
        /^\d{2}\/\d{2}\/\d{4}$/, // MM/DD/YYYY
        /^\d{2}-\d{2}-\d{4}$/,   // MM-DD-YYYY
      ];
      
      let isDate = false;
      for (const pattern of datePatterns) {
        if (pattern.test(searchTerm)) {
          isDate = true;
          // Convert to YYYY-MM-DD format for Practice Fusion
          let formattedDate = searchTerm;
          if (searchTerm.includes('/')) {
            // MM/DD/YYYY -> YYYY-MM-DD
            const [month, day, year] = searchTerm.split('/');
            formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          } else if (searchTerm.match(/^\d{2}-\d{2}-\d{4}$/)) {
            // MM-DD-YYYY -> YYYY-MM-DD
            const [month, day, year] = searchTerm.split('-');
            formattedDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
          }
          searchBody.birthDate = formattedDate;
          break;
        }
      }
      
      if (!isDate) {
        // Check if it looks like an EHR record number (contains letters and numbers, no spaces)
        const isEhrNumber = /^[A-Za-z0-9]+$/.test(searchTerm) && /\d/.test(searchTerm) && /[A-Za-z]/.test(searchTerm);
        
        if (isEhrNumber) {
          searchBody.patientRecordNumber = searchTerm;
        } else {
          searchBody.firstOrLastName = searchTerm;
        }
      }

      const ehrResponse = await axios.post(`${API_URL}/api/integrations/practice-fusion/search`, searchBody);
      const pfPatients = ehrResponse.data.patients || [];
      
      // Check which PF patients already exist in local DB by name + DOB
      let existingMap = {}; // maps "name|dob" -> local patient id
      if (pfPatients.length > 0) {
        try {
          const checkPayload = pfPatients.map(pf => ({
            name: `${pf.firstName || ''} ${pf.lastName || ''}`.trim(),
            dob: pf.birthDate || ''
          }));
          const checkRes = await axios.post(`${API_URL}/api/patients/check-existing`, checkPayload, {
            headers: { Authorization: `Bearer ${localStorage.getItem('clinic_token')}` }
          });
          existingMap = checkRes.data || {};
        } catch (localError) {
          console.warn('Could not check existing patients:', localError);
        }
      }
      
      // Mark PF results with existing status and local patient ID
      const enrichedResults = pfPatients.map(pf => {
        const name = `${pf.firstName || ''} ${pf.lastName || ''}`.trim().toLowerCase();
        const dob = (pf.birthDate || '').split('T')[0];
        const key = `${name}|${dob}`;
        const localId = existingMap[key];
        return { ...pf, already_exists: !!localId, local_patient_id: localId || null };
      });
      
      setEhrResults(enrichedResults);
      
      const newCount = enrichedResults.filter(r => !r.already_exists).length;
      if (newCount === 0 && pfPatients.length > 0) {
        toast.info(`Found ${pfPatients.length} patients, all already exist in local database`);
      } else if (pfPatients.length === 0) {
        toast.info('No patients found in Practice Fusion');
      }
    } catch (error) {
      console.error('PF search error:', error);
      // Handle validation errors which may be arrays
      let errorMessage = 'Failed to search Practice Fusion';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
      }
      toast.error(errorMessage);
    } finally {
      setEhrSearching(false);
    }
  };

  const searchPatientsWithEhrFallback = async (searchTerm) => {
    try {
      setLoading(true);
      setEhrResults([]); // Clear previous EHR results
      
      // Always search from page 1 — search results shouldn't depend on current pagination
      const patientsRes = await patientsAPI.getAll({ 
        search: searchTerm, 
        page: 1, 
        page_size: 100 
      });
      
      const paginatedData = patientsRes.data;
      const localPatients = paginatedData.items || [];
      setPatients(localPatients);
      setTotalItems(paginatedData.total || 0);
      setTotalPages(paginatedData.total_pages || 0);
      
      // If no local results and Practice Fusion is enabled, search EHR
      if (localPatients.length === 0 && hasPracticeFusion && searchTerm.length >= 2) {
        setEhrSearching(true);
        try {
          const ehrResponse = await axios.post(`${API_URL}/api/integrations/practice-fusion/search`, {
            firstOrLastName: searchTerm
          });
          setEhrResults(ehrResponse.data.patients || []);
        } catch (ehrError) {
          // Silently handle EHR errors - just show no EHR results
          console.log('EHR search failed:', ehrError.response?.data?.detail || ehrError.message);
          setEhrResults([]);
        } finally {
          setEhrSearching(false);
        }
      }
    } catch (error) {
      toast.error('Failed to search patients');
    } finally {
      setLoading(false);
    }
  };

  const fetchStaticData = async () => {
    try {
      const [locationsRes, doctorsRes] = await Promise.all([
        locationsAPI.getAll({ page_size: 100 }),
        doctorsAPI.getAll(),
      ]);
      const locData = locationsRes.data.items || locationsRes.data;
      const allLocations = Array.isArray(locData) ? locData : [];
      setLocations(filterUserLocations(allLocations, user));
      const docData = doctorsRes.data.items || doctorsRes.data;
      setDoctors(Array.isArray(docData) ? docData : []);
    } catch (error) {
      console.error('Failed to fetch static data', error);
    }
  };

  const fetchPatientData = async () => {
    try {
      setLoading(true);
      setActiveVisits({}); // Reset cache for new patient list
      const patientsRes = await patientsAPI.getAll({ search: submittedQuery || undefined, page: currentPage, page_size: pageSize });
      const paginatedData = patientsRes.data;
      setPatients(paginatedData.items || []);
      setTotalItems(paginatedData.total || 0);
      setTotalPages(paginatedData.total_pages || 0);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async () => {
    await fetchPatientData();
  };

  // Auto-mask date-like input as MM/DD/YYYY so DOB search is an exact date; names pass through.
  const handleSearchInput = (e) => {
    let v = e.target.value;
    if (/^[\d/]*$/.test(v) && /\d/.test(v)) {
      const d = v.replace(/\D/g, '').slice(0, 8);
      if (d.length > 4) v = `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
      else if (d.length > 2) v = `${d.slice(0, 2)}/${d.slice(2)}`;
      else v = d;
    }
    setSearch(v);
  };

  // Click-to-search trigger (button / Enter key). No typeahead.
  const runSearch = () => {
    const q = search.trim();
    if (pfOnlyMode && q.length < 2) {
      toast.info('Enter at least 2 characters to search Practice Fusion');
      return;
    }
    setCurrentPage(1);
    setSubmittedQuery(q);
  };

  const checkPatientActiveVisit = async (patientId) => {
    if (activeVisits[patientId] !== undefined) return; // Already checked
    try {
      const res = await patientsAPI.getActiveVisit(patientId);
      setActiveVisits(prev => ({
        ...prev,
        [patientId]: res.data.has_active_visit ? res.data.status : null
      }));
    } catch {
      setActiveVisits(prev => ({ ...prev, [patientId]: null }));
    }
  };

  const fetchPatients = async () => {
    await fetchData();
  };

  // Import a single patient from EHR
  const handleImportEhrPatient = async (ehrPatient) => {
    setEhrImporting(ehrPatient.patientPracticeGuid);
    try {
      const response = await axios.post(`${API_URL}/api/integrations/practice-fusion/import`, {
        patients: [ehrPatient],
        location_id: workingLocation?.id || null
      });
      
      const { imported, skipped, summary } = response.data;
      
      if (summary.imported_count > 0) {
        toast.success(`Successfully imported ${ehrPatient.firstName} ${ehrPatient.lastName}`);
        // Remove imported patient from EHR results
        setEhrResults(prev => prev.filter(p => p.patientPracticeGuid !== ehrPatient.patientPracticeGuid));
        // Refresh local patients list
        await fetchData();
      } else if (summary.skipped_count > 0) {
        toast.info('Patient already exists in your records');
      }
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to import patient');
    } finally {
      setEhrImporting(null);
    }
  };

  // Navigate to an existing patient: search by name, open their accordion
  const goToExistingPatient = async (ehrPatient) => {
    const name = `${ehrPatient.firstName || ''} ${ehrPatient.lastName || ''}`.trim();
    setPfOnlyMode(false); // Switch to local DB search
    setEhrResults([]);
    setSearch(name);
    setSubmittedQuery(name);
    setCurrentPage(1);
    // Wait for search to trigger and results to load, then open the accordion
    setTimeout(() => {
      if (ehrPatient.local_patient_id) {
        setOpenAccordionId(ehrPatient.local_patient_id);
        checkPatientActiveVisit(ehrPatient.local_patient_id);
      }
    }, 1200);
  };


  // Format EHR date for display
  const formatEhrDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return dateStr.split('T')[0];
    } catch {
      return dateStr;
    }
  };

  // Format EHR gender for display
  const formatEhrGender = (gender) => {
    if (gender === 0) return 'Male';
    if (gender === 1) return 'Female';
    return 'Other';
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPatient) {
        await patientsAPI.update(editingPatient.id, formData);
        toast.success('Patient updated successfully');
      } else {
        await patientsAPI.create(formData);
        toast.success('Patient registered successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchPatients();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to save patient');
    }
  };

  const handleCheckIn = async (e) => {
    e.preventDefault();
    if (!selectedPatientForCheckIn || !workingLocation?.id || checkingIn) return;
    
    setCheckingIn(true);
    try {
      await visitsAPI.create({
        patient_id: selectedPatientForCheckIn.id,
        location_id: workingLocation.id,
        doctor_id: checkInData.doctor_id || null,
        visit_type: checkInData.visit_type || 'FU'
      });
      toast.success(`${selectedPatientForCheckIn.full_name} checked in successfully!`);
      setCheckInDialogOpen(false);
      setSelectedPatientForCheckIn(null);
      setCheckInData({ doctor_id: '', visit_type: 'FU' });
      // Navigate to Visits page after successful check-in
      navigate('/visits');
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : 'Failed to check in patient');
    } finally {
      setCheckingIn(false);
    }
  };

  const resetForm = () => {
    setFormData({
      full_name: '',
      date_of_birth: '',
      phone: '',
      email: '',
      address: '',
      insurance_provider: '',
      insurance_number: ''
    });
    setEditingPatient(null);
  };

  const openEditDialog = (patient) => {
    setEditingPatient(patient);
    setFormData({
      full_name: patient.full_name,
      date_of_birth: patient.date_of_birth,
      phone: patient.phone,
      email: patient.email || '',
      address: patient.address || '',
      insurance_provider: patient.insurance_provider || '',
      insurance_number: patient.insurance_number || ''
    });
    setDialogOpen(true);
  };

  const openCheckInDialog = (patient) => {
    setSelectedPatientForCheckIn(patient);
    setCheckInData({ doctor_id: '', visit_type: 'FU' });
    setCheckInDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="patients-page">
      {/* Header */}
      <div className="flex flex-col gap-4">
        {/* Practice Fusion Toggle */}
        {hasPracticeFusion && (
          <div className="flex items-center gap-3">
            <button
              onClick={() => {
                const newMode = !pfOnlyMode;
                setPfOnlyMode(newMode);
                setEhrResults([]);
                setSearch('');
                setSubmittedQuery('');
                if (newMode) {
                  setPatients([]);
                }
              }}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                pfOnlyMode ? 'bg-blue-600' : 'bg-gray-200'
              }`}
              role="switch"
              aria-checked={pfOnlyMode}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  pfOnlyMode ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
              <Database className="w-4 h-4" />
              Search Practice Fusion Only
            </span>
            {pfOnlyMode && (
              <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">
                EHR Mode
              </span>
            )}
          </div>
        )}

        {/* Search Section - Same field for both modes */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <div className="relative flex-1">
            <div className="absolute left-2.5 top-1/2 -translate-y-1/2 group z-10">
              <svg className="w-4 h-4 text-slate-400 cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="absolute left-0 top-full mt-2 hidden group-hover:block w-60 p-3 bg-slate-800 text-white text-xs rounded-lg shadow-lg z-50">
                <p className="font-semibold mb-2">Search by:</p>
                <ul className="space-y-1.5">
                  <li>• <span className="text-blue-300">Name:</span> first or last name</li>
                  {pfOnlyMode && <li>• <span className="text-blue-300">EHR #:</span> e.g., VM74516</li>}
                  <li>• <span className="text-blue-300">DOB:</span> exact date as MM/DD/YYYY</li>
                </ul>
              </div>
            </div>
            <Input
              placeholder="Search by name or DOB (MM/DD/YYYY)"
              value={search}
              onChange={handleSearchInput}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runSearch(); } }}
              className={`h-11 pl-9 pr-14 bg-white border-slate-200 focus:border-blue-500 ${pfOnlyMode ? 'border-blue-300' : ''}`}
              data-testid="patient-search"
              title="Search by patient name or exact DOB (MM/DD/YYYY). Press Enter or click the search icon."
            />
            <button
              type="button"
              onClick={runSearch}
              data-testid="patient-search-btn"
              title="Search"
              aria-label="Search patients"
              className="absolute right-1.5 top-1/2 -translate-y-1/2 flex items-center justify-center h-9 w-9 rounded-md text-slate-500 hover:text-white hover:bg-blue-600 active:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {ehrSearching ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <Search className="w-5 h-5" />
              )}
            </button>
            </div>
            {submittedQuery && (
              <Button
                type="button"
                variant="outline"
                onClick={() => { setSearch(''); setSubmittedQuery(''); }}
                data-testid="patient-search-clear-btn"
                className="shrink-0"
              >
                Clear
              </Button>
            )}
          </div>
          
          {canEdit && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="add-patient-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Patient
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingPatient ? 'Edit Patient' : 'Register New Patient'}</DialogTitle>
                <DialogDescription>
                  {editingPatient ? 'Update patient information' : 'Enter patient details to create a new record'}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <DialogBody className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <Label htmlFor="full_name" className="text-slate-700">Full Name *</Label>
                      <Input
                        id="full_name"
                        placeholder="Enter patient's full name"
                        value={formData.full_name}
                        onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                        required
                        className="mt-1.5"
                        data-testid="patient-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="date_of_birth" className="text-slate-700">Date of Birth *</Label>
                      <Input
                        id="date_of_birth"
                        type="date"
                        value={formData.date_of_birth}
                        onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                        required
                        className="mt-1.5"
                        data-testid="patient-dob-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone" className="text-slate-700">Phone *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        placeholder="(555) 123-4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="mt-1.5"
                        data-testid="patient-phone-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="email" className="text-slate-700">Email</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="patient@example.com"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="mt-1.5"
                        data-testid="patient-email-input"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label htmlFor="address" className="text-slate-700">Address</Label>
                      <Input
                        id="address"
                        placeholder="Street address, city, state"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="mt-1.5"
                        data-testid="patient-address-input"
                      />
                    </div>
                  </div>
                  
                  <div className="pt-2">
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-3">Insurance Information</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="insurance_provider" className="text-slate-700">Provider</Label>
                        <Input
                          id="insurance_provider"
                          placeholder="e.g., BlueCross"
                          value={formData.insurance_provider}
                          onChange={(e) => setFormData({ ...formData, insurance_provider: e.target.value })}
                          className="mt-1.5"
                          data-testid="patient-insurance-provider-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="insurance_number" className="text-slate-700">Policy Number</Label>
                        <Input
                          id="insurance_number"
                          placeholder="Policy or member ID"
                          value={formData.insurance_number}
                          onChange={(e) => setFormData({ ...formData, insurance_number: e.target.value })}
                          className="mt-1.5"
                          data-testid="patient-insurance-number-input"
                        />
                      </div>
                    </div>
                  </div>
                </DialogBody>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="border-slate-200">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-gradient-to-r from-blue-700 to-indigo-700 hover:from-blue-800 hover:to-indigo-800 text-white shadow-md" data-testid="save-patient-btn">
                    {editingPatient ? 'Update Patient' : 'Register Patient'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      {/* Check-In Dialog */}
      <Dialog open={checkInDialogOpen} onOpenChange={setCheckInDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Check In Patient</DialogTitle>
            <DialogDescription>
              Check in <strong>{selectedPatientForCheckIn?.full_name}</strong> (MRN: {selectedPatientForCheckIn?.mrn})
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCheckIn}>
            <DialogBody className="space-y-4">
              {/* Location Info - Using selected location from header */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                <p className="text-xs text-slate-500 uppercase font-medium mb-1">Location</p>
                <p className="font-medium text-slate-900">{workingLocation?.name || 'No location selected'}</p>
              </div>
              <div>
                <Label className="text-slate-700">Visit Type *</Label>
                <Select 
                  value={checkInData.visit_type} 
                  onValueChange={(val) => setCheckInData({ ...checkInData, visit_type: val, doctor_id: val === 'NO' ? '' : checkInData.doctor_id })}
                >
                  <SelectTrigger className="mt-1.5" data-testid="checkin-visit-type-select">
                    <SelectValue placeholder="Select visit type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FU">
                      <span className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-medium">FU</span>
                        Follow Up
                      </span>
                    </SelectItem>
                    <SelectItem value="NP">
                      <span className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs font-medium">NP</span>
                        New Patient
                      </span>
                    </SelectItem>
                    <SelectItem value="NC">
                      <span className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">NC</span>
                        New Consultation
                      </span>
                    </SelectItem>
                    <SelectItem value="LR">
                      <span className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs font-medium">LR</span>
                        Lab Results
                      </span>
                    </SelectItem>
                    <SelectItem value="US">
                      <span className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-cyan-100 text-cyan-700 rounded text-xs font-medium">US</span>
                        Ultrasound
                      </span>
                    </SelectItem>
                    <SelectItem value="NO">
                      <span className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded text-xs font-medium">NO</span>
                        Nursing Only
                      </span>
                    </SelectItem>
                    <SelectItem value="PH">
                      <span className="flex items-center gap-2">
                        <span className="px-2 py-0.5 bg-teal-100 text-teal-700 rounded text-xs font-medium">PH</span>
                        Pharmacy
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {/* Doctor Assignment - Hidden for Nursing Only visits */}
              {requiresDoctor && (
                <div>
                  <Label className="text-slate-700">Assign Doctor *</Label>
                  <Select 
                    value={checkInData.doctor_id} 
                    onValueChange={(val) => setCheckInData({ ...checkInData, doctor_id: val })}
                  >
                    <SelectTrigger className="mt-1.5" data-testid="checkin-doctor-select">
                      <SelectValue placeholder="Select doctor" />
                    </SelectTrigger>
                    <SelectContent>
                      {doctors.map((d) => (
                        <SelectItem key={d.id} value={d.id}>{d.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Info message for Nursing Only */}
              {!requiresDoctor && (
                <div className="bg-rose-50 border-rose-200 border rounded-lg p-3">
                  <p className="text-sm text-rose-700">
                    <strong>Nursing Only Visit:</strong> No doctor assignment required. This visit can be closed without billing.
                  </p>
                </div>
              )}
            </DialogBody>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setCheckInDialogOpen(false)} className="border-slate-200">
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-blue-600 hover:bg-blue-700 text-white"
                disabled={!workingLocation?.id || (requiresDoctor && !checkInData.doctor_id) || checkingIn}
                data-testid="confirm-checkin-btn"
              >
                <UserCheck className="w-4 h-4 mr-2" />
                {checkingIn ? 'Checking In...' : 'Check In'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Patients Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="spinner"></div>
            </div>
          ) : patients.length === 0 && ehrResults.length === 0 ? (
            <div className="empty-state py-16">
              <User className="empty-state-icon" />
              <p className="empty-state-title">No patients found</p>
              <p className="empty-state-text">
                {search ? 'Try a different search term' : 'Register your first patient'}
              </p>
              {ehrSearching && (
                <div className="mt-4 flex items-center justify-center gap-2 text-blue-600">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Searching Practice Fusion...</span>
                </div>
              )}
            </div>
          ) : patients.length === 0 && ehrResults.length > 0 ? (
            /* No local results but EHR results found */
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-full text-sm font-medium mb-3">
                  <Database className="w-4 h-4" />
                  Found in Practice Fusion EHR
                </div>
                <p className="text-slate-500 text-sm">
                  No local patients match your search. {ehrResults.length} patient(s) found in Practice Fusion.
                </p>
              </div>
              
              {/* EHR Results List */}
              <div className="space-y-3">
                {ehrResults.map((ehrPatient) => (
                  <div 
                    key={ehrPatient.patientPracticeGuid}
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border border-slate-200 hover:border-blue-300 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {ehrPatient.firstName?.[0]}{ehrPatient.lastName?.[0]}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">
                            {ehrPatient.firstName} {ehrPatient.lastName}
                          </span>
                          {ehrPatient.patientRecordNumber && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                              EHR: {ehrPatient.patientRecordNumber}
                            </span>
                          )}
                          {ehrPatient.already_exists && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                              Already imported
                            </span>
                          )}
                        </div>
                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-500 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatEhrDate(ehrPatient.birthDate)}
                          </span>
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {formatEhrGender(ehrPatient.gender)}
                          </span>
                          {ehrPatient.mobilePhone && (
                            <span className="flex items-center gap-1">
                              <Phone className="w-3 h-3" />
                              {ehrPatient.mobilePhone}
                            </span>
                          )}
                          {ehrPatient.city && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {ehrPatient.city}, {ehrPatient.state}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    {ehrPatient.already_exists ? (
                      <Button
                        onClick={() => goToExistingPatient(ehrPatient)}
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid={`goto-existing-patient-${ehrPatient.patientPracticeGuid}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Exists — Go to Patient
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleImportEhrPatient(ehrPatient)}
                        disabled={ehrImporting === ehrPatient.patientPracticeGuid}
                        size="sm"
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                        data-testid={`import-ehr-patient-${ehrPatient.patientPracticeGuid}`}
                      >
                        {ehrImporting === ehrPatient.patientPracticeGuid ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                            Importing...
                          </>
                        ) : (
                          <>
                            <Download className="w-4 h-4 mr-1" />
                            Import
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-2" value={openAccordionId} onValueChange={(val) => { setOpenAccordionId(val); if (val) checkPatientActiveVisit(val); }}>
              {patients.map((patient) => (
                <AccordionItem 
                  key={patient.id} 
                  value={patient.id}
                  className="border rounded-lg px-4 bg-white shadow-sm"
                  data-testid={`patient-row-${patient.id}`}
                >
                  <AccordionTrigger className="hover:no-underline py-4">
                    <div className="flex items-center justify-between w-full pr-4">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <span className="text-sm font-medium text-slate-600">
                            {patient.full_name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
                          </span>
                        </div>
                        <div className="text-left">
                          <p className="font-medium text-slate-900">{patient.full_name}</p>
                          <div className="flex items-center gap-2 text-xs text-slate-500">
                            <span>MRN: {patient.mrn}</span>
                            {patient.practice_fusion_record_number && (
                              <span className="px-1.5 py-0.5 bg-blue-50 text-blue-600 rounded">
                                EHR: {patient.practice_fusion_record_number}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {activeVisits[patient.id] && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                            {activeVisits[patient.id].replace(/_/g, ' ')}
                          </span>
                        )}
                        {patient.insurance_provider && (
                          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                            {patient.insurance_provider}
                          </span>
                        )}
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="pt-2 pb-4 border-t">
                      {inlineEditing === patient.id ? (
                        /* Inline Edit Mode */
                        <div className="space-y-4 mt-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label className="text-slate-600 text-xs">Full Name</Label>
                              <Input
                                value={inlineFormData.full_name}
                                onChange={(e) => setInlineFormData({...inlineFormData, full_name: e.target.value})}
                                className="mt-1 h-9"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-600 text-xs">Date of Birth</Label>
                              <Input
                                type="date"
                                value={inlineFormData.date_of_birth}
                                onChange={(e) => setInlineFormData({...inlineFormData, date_of_birth: e.target.value})}
                                className="mt-1 h-9"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-600 text-xs">Phone</Label>
                              <Input
                                value={inlineFormData.phone}
                                onChange={(e) => setInlineFormData({...inlineFormData, phone: e.target.value})}
                                className="mt-1 h-9"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-600 text-xs">Email</Label>
                              <Input
                                type="email"
                                value={inlineFormData.email}
                                onChange={(e) => setInlineFormData({...inlineFormData, email: e.target.value})}
                                className="mt-1 h-9"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-600 text-xs">Insurance Provider</Label>
                              <Input
                                value={inlineFormData.insurance_provider}
                                onChange={(e) => setInlineFormData({...inlineFormData, insurance_provider: e.target.value})}
                                className="mt-1 h-9"
                              />
                            </div>
                            <div>
                              <Label className="text-slate-600 text-xs">Policy Number</Label>
                              <Input
                                value={inlineFormData.insurance_number}
                                onChange={(e) => setInlineFormData({...inlineFormData, insurance_number: e.target.value})}
                                className="mt-1 h-9"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-slate-600 text-xs">Address</Label>
                            <Input
                              value={inlineFormData.address}
                              onChange={(e) => setInlineFormData({...inlineFormData, address: e.target.value})}
                              className="mt-1 h-9"
                            />
                          </div>
                          <div className="flex items-center gap-2 pt-2">
                            <Button 
                              size="sm" 
                              onClick={() => saveInlineEdit(patient.id)}
                              className="bg-teal-600 hover:bg-teal-700 text-white"
                            >
                              <Save className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={cancelInlineEdit}
                              className="text-slate-700 border-slate-200"
                            >
                              <X className="w-4 h-4 mr-1" />
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* View Mode */
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                            <div className="flex items-center gap-2 text-sm">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">DOB:</span>
                              <span className="text-slate-900">{patient.date_of_birth || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Phone className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">Phone:</span>
                              <span className="text-slate-900">{patient.phone || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <Mail className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">Email:</span>
                              <span className="text-slate-900">{patient.email || 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              <CreditCard className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">Policy:</span>
                              <span className="text-slate-900">{patient.insurance_number || 'N/A'}</span>
                            </div>
                          </div>
                          {patient.address && (
                            <div className="flex items-center gap-2 text-sm mt-3">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              <span className="text-slate-600">Address:</span>
                              <span className="text-slate-900">{patient.address}</span>
                            </div>
                          )}
                          {canEdit && (
                            <div className="flex items-center gap-2 mt-4 pt-4 border-t">
                              {activeVisits[patient.id] !== undefined && !activeVisits[patient.id] && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => openCheckInDialog(patient)}
                                  className="text-teal-700 border-teal-200 hover:bg-teal-50 bg-teal-50"
                                  data-testid={`checkin-patient-${patient.id}`}
                                >
                                  <UserCheck className="w-4 h-4 mr-1" />
                                  Check In
                                </Button>
                              )}
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => startInlineEdit(patient)}
                                className="text-slate-700 border-slate-200 hover:bg-slate-50"
                                data-testid={`edit-patient-${patient.id}`}
                              >
                                <Edit className="w-4 h-4 mr-1" />
                                Edit
                              </Button>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </CardContent>
        
        {/* Pagination - show for local search, hide for Practice Fusion only mode */}
        {!pfOnlyMode && totalItems > 0 && (
          <DataPagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </Card>
    </div>
  );
};

export default Patients;
